import { NotificationService, type NotificationResponse } from '@acme/api';
import { GroupedList, Pagination } from '@acme/foundation';
import { Button } from 'flowbite-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const formatDateTime = (dateString?: string | null) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isToday = (dateString?: string | null) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const NotificationCard = ({
  notification,
}: {
  notification: NotificationResponse;
}) => {
  const hasHeader = !!notification.subject || !!notification.applicationCode;
  const isRead = !!notification.readingAt;
  const bgColor = isRead ? 'bg-white' : 'bg-white';

  return (
    <div
      className={`${bgColor} rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] p-4 flex gap-3`}
    >
      <div className="flex flex-col gap-1 grow">
        {hasHeader && (
          <p className="text-sm font-normal text-blue-600 leading-tight">
            <Link
              to={`/application/${notification.applicationId}`}
              className="text-sm hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {notification.applicationCode
                ? `Заявка: ${notification.applicationCode}`
                : notification.subject}
            </Link>
          </p>
        )}
        <p className="text-base font-normal text-gray-500 leading-6">
          {notification.description || notification.subject}
        </p>
        <div className="flex gap-1.5 items-center">
          <svg
            className="w-3 h-3 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm font-normal text-gray-500 leading-tight">
            {formatDateTime(notification.createdAt)}
          </p>
        </div>
      </div>
    </div>
  );
};

export const NotificationsPage = () => {
  const [notifications, setNotifications] = useState<NotificationResponse[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    loadNotifications();
  }, [pageNumber]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response =
        await NotificationService.getApiV1NotificationNotification({
          pageNumber,
          pageSize,
        });

      if (response.data) {
        const items = response.data?.items || [];
        setNotifications(items);
        setTotalCount(response.data?.totalCount || 0);

        const unreadIds = items
          .filter((n) => !n.readingAt && n.id)
          .map((n) => n.id as string);
        if (unreadIds.length > 0) {
          try {
            await NotificationService.postApiV1NotificationNotificationRead({
              requestBody: { ids: unreadIds },
            });
            const readAt = new Date().toISOString();
            setNotifications((prev) =>
              prev.map((n) =>
                unreadIds.includes(n.id as string)
                  ? { ...n, readingAt: readAt }
                  : n
              )
            );
          } catch {
            // оставляем items как есть
          }
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки уведомлений:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupedNotifications = useMemo(() => {
    const todayItems: NotificationResponse[] = [];
    const allTimeItems: NotificationResponse[] = [];

    notifications.forEach((notification) => {
      if (isToday(notification.createdAt)) {
        todayItems.push(notification);
      } else {
        allTimeItems.push(notification);
      }
    });

    const groups: Array<{
      key: string;
      label: string;
      items: NotificationResponse[];
    }> = [];

    if (todayItems.length > 0) {
      groups.push({
        key: 'today',
        label: 'Сегодня',
        items: todayItems,
      });
    }

    if (allTimeItems.length > 0) {
      groups.push({
        key: 'all-time',
        label: 'За все время',
        items: allTimeItems,
      });
    }

    return groups;
  }, [notifications]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto w-full">
      <div className="flex items-start justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Уведомления</h1>
      </div>

      <div className="flex gap-4 items-center h-[69px] px-0 py-4">
        <Button color="gray" className="bg-white border border-gray-200">
          <span className="text-sm font-medium text-gray-800">
            Отображать: <span>Все</span>
          </span>
          <svg
            className="w-3 h-3 ml-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </Button>
        <div className="bg-white border border-gray-300 rounded-lg h-[37px] flex items-center px-4 py-3.5 w-64">
          <svg
            className="w-4 h-4 mr-2 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-sm font-medium text-gray-500">Выберите период</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <p className="text-sm text-gray-500">Загрузка...</p>
        </div>
      ) : (
        <>
          <GroupedList
            groups={groupedNotifications}
            renderItem={(item) => <NotificationCard notification={item} />}
            emptyMessage="Нет уведомлений"
          />

          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                pageNumber={pageNumber}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={setPageNumber}
                isLoading={loading}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
